import type { AuditEntry } from '@/api/ticketsApi'
import { priorityLabel, statusLabel } from '@/lib/ticketLabels'
import { dayLabel } from '@/lib/threadView'

/**
 * The audit trail as people read it: what kind of event (for the filter
 * chips), one sentence (an i18n key + params), and the entries grouped by
 * day. The hash-chained record itself is untouched; the tab still shows every
 * field on demand.
 */

export type AuditCategory = 'status' | 'messages' | 'files' | 'changes' | 'system'

const CATEGORY: Record<string, AuditCategory> = {
  ticket_created: 'changes',
  field_change: 'changes',
  record_linked: 'changes',
  deviation_escalated: 'changes',
  routed: 'changes',
  status_change: 'status',
  resolved: 'status',
  reopened: 'status',
  auto_reopened: 'status',
  auto_closed: 'status',
  follow_up_created: 'status',
  follow_up_due: 'status',
  no_response_flagged: 'status',
  message_added: 'messages',
  message_edited: 'messages',
  mentioned: 'messages',
  pending_reminder: 'messages',
  attachment_added: 'files',
  attachment_downloaded: 'files',
  thread_exported: 'files',
  // Bookkeeping the desk does on its own.
  sla_recomputed: 'system',
  sla_warning: 'system',
  sla_breached: 'system',
  first_response: 'system',
}

export function categoryOf(e: Pick<AuditEntry, 'eventType'>): AuditCategory {
  return CATEGORY[e.eventType] ?? 'changes'
}

/** SLA recalculations and similar bookkeeping, hidden unless asked for. */
export const isSystemEvent = (e: Pick<AuditEntry, 'eventType'>) => categoryOf(e) === 'system'

export interface AuditSentence {
  key: string
  params: Record<string, string>
}

const FIELD_KEY: Record<string, string> = {
  assigneeId: 'assignee',
  teamId: 'team',
  tenantId: 'tenant',
  environmentId: 'environment',
  componentId: 'component',
  gxpImpact: 'gxpImpact',
  gxpNote: 'gxpNote',
  priority: 'priority',
  subject: 'subject',
  type: 'type',
  followUpAt: 'followUp',
  formRef: 'form',
  formVersion: 'formVersion',
  watcherContactIds: 'watchers',
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {})
const str = (v: unknown): string => (v === null || v === undefined || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v))

/**
 * One readable sentence for an entry. `name` resolves an id (an assignee, a
 * team) to a name where the caller knows it.
 */
export function describe(e: AuditEntry, name: (id: string) => string | undefined = () => undefined): AuditSentence {
  const nv = obj(e.newValue)
  const meta = obj(e.metadata)
  const k = (key: string, params: Record<string, string> = {}): AuditSentence => ({ key: `audit.event.${key}`, params })
  switch (e.eventType) {
    case 'ticket_created':
      return k('ticket_created', { channel: str(nv.channel) })
    case 'status_change': {
      const action = typeof meta.action === 'string' ? meta.action : 'update'
      const params = { from: statusLabel(str(e.oldValue)), to: statusLabel(str(e.newValue)) }
      return action === 'auto_resume' ? k('status_auto_resume', params) : action === 'follow_up_due' ? k('status_follow_up_due', params) : k('status_change', params)
    }
    case 'field_change': {
      const field = e.field ?? ''
      const show = (v: unknown) => {
        if (v === null || v === undefined || v === '') return '—'
        if (field === 'priority') return priorityLabel(String(v))
        if (field === 'followUpAt' && typeof v === 'string') return new Date(v).toLocaleString()
        if (typeof v === 'string' && /^[0-9a-f-]{36}$/.test(v)) return name(v) ?? `${v.slice(0, 8)}…`
        return str(v)
      }
      return k('field_change', { field: FIELD_KEY[field] ?? field, from: show(e.oldValue), to: show(e.newValue) })
    }
    case 'message_added': {
      const kind = typeof nv.kind === 'string' ? nv.kind : 'message'
      return k(`message_${['agent_reply', 'customer_reply', 'internal_note', 'system_event'].includes(kind) ? kind : 'other'}`)
    }
    case 'attachment_added':
      return k('attachment_added', { name: str(nv.fileName) })
    case 'attachment_downloaded':
      return k(meta.inline ? 'attachment_previewed' : 'attachment_downloaded', { name: str(nv.fileName) })
    case 'sla_recomputed':
      return k('sla_recomputed')
    default:
      return { key: `audit.event.${e.eventType}`, params: {} }
  }
}

export interface AuditDay<T> {
  day: string
  entries: T[]
}

/** Entries (oldest first, as the API sends them) grouped by local day, newest day first. */
export function groupByDay<T extends Pick<AuditEntry, 'at'>>(entries: T[], timeZone: string): AuditDay<T>[] {
  const days: AuditDay<T>[] = []
  for (const e of [...entries].reverse()) {
    const day = dayLabel(e.at, timeZone)
    const last = days[days.length - 1]
    if (last && last.day === day) last.entries.push(e)
    else days.push({ day, entries: [e] })
  }
  return days
}
